import React, { ReactElement, Component, useEffect, useContext, useMemo } from "react";
import { injectIntl } from 'react-intl';
import './nodes-management.less';
import { ItemsContext, item } from "../../contexts/items";
import { CardSelectableContext } from "../../contexts/card-selectable";
import { SearchTitleContext } from "../../contexts/search-title";
import { NodeContext, nodes_type_item } from "../../contexts/nodes";
import { http_get, http_request, destroy_worker } from "../../worker/http-func";
import { IsInTabsContext } from "../../contexts/is-in-tabs";
import TableList from "../../components/table-list/table-list";
import { TableDescContext } from "../../contexts/table-desc";
import { IsFreshContext } from '../../contexts/is_fresh';
import { ClusterSelectedContext } from "../../contexts/cluster-selected";
import { UserInfoContext, IUserInfoType } from "../../contexts/user-info";
import { clusters_type_item, ClusterContext } from "../../contexts/clusters";
import { DatacenternameContext } from "../../contexts/datacenter-name";
import { clusters_for_page_type } from "../../contexts/clusters-for-page";
import { ClustersForPageContext } from "../../contexts/clusters-for-page";

interface node_new {
    name: string;
    uid: string;
    selfLink: string;
    resourceVersion: string;
    creationTimestamp: string;
}

const NodesManagement: React.FC = (props: any): ReactElement<any, string | (new (props: any) => Component<any, any, any>)> | null => {
    const [clusters, set_clusters]: [clusters_type_item[], React.Dispatch<React.SetStateAction<clusters_type_item[]>>] = useContext(ClusterContext),
        [nodes, set_nodes]: [nodes_type_item[], React.Dispatch<React.SetStateAction<nodes_type_item[]>>] = useContext(NodeContext),
        user_info: IUserInfoType = useContext(UserInfoContext)[0],
        set_items: React.Dispatch<React.SetStateAction<item[]>> = useContext(ItemsContext)[1],
        set_card_selectable: React.Dispatch<React.SetStateAction<boolean>> = useContext(CardSelectableContext)[1],
        set_search_title: React.Dispatch<React.SetStateAction<string>> = useContext(SearchTitleContext)[1],
        set_is_in_tabs: React.Dispatch<React.SetStateAction<boolean>> = useContext(IsInTabsContext)[1],
        set_table_desc: React.Dispatch<React.SetStateAction<string[]>> = useContext(TableDescContext)[1],
        is_fresh: boolean = useContext(IsFreshContext)[0],
        cluster_selected: item | undefined = useContext(ClusterSelectedContext)[0],
        set_cluster_selected: React.Dispatch<React.SetStateAction<item | undefined>> = useContext(ClusterSelectedContext)[1],
        clusters_for_page: clusters_for_page_type | undefined = useContext(ClustersForPageContext)[0],
        datacenter_name: string | undefined = useContext(DatacenternameContext)[0];

    useEffect(() => () => { destroy_worker() }, []);
    const { formatMessage } = useMemo(() => props.intl, [props]);

    useEffect((): void => {
        user_info.role !== '' && datacenter_name && http_get('nodes_page', ['clusters', set_clusters], user_info.role, user_info.subscription_id, datacenter_name, '');
    }, [set_clusters, user_info.role, user_info.subscription_id, datacenter_name]);

    // 切换订阅号，当没有权限时，依然能正常显示cluster选择框和搜索框
    useEffect((): void => {
        if (clusters_for_page?.clusters_for_nodes_page === undefined && clusters_for_page?.clusters_for_monitor_page && clusters_for_page?.clusters_for_monitor_page?.length > 0) {
            set_clusters(clusters_for_page?.clusters_for_monitor_page);
        }
    }, [clusters_for_page, set_clusters, user_info.subscription_id]);

    useEffect((): void => {
        // const pathname = props.history.location.pathname,
        //   push = () => { if (pathname.includes('/cluster-info/') || pathname.includes('/workspace-info/') || pathname.includes('/namespace-info/')) props.history.push('/clusters') };
        if (clusters?.length) {
            const data: string = sessionStorage.getItem('cluster') || '',
                selected = clusters.find((cluster: clusters_type_item) => cluster.metadata && cluster.metadata.name === data);
            if (selected) set_cluster_selected(selected.metadata);
            else {
                const { metadata }: clusters_type_item = clusters[0];
                set_cluster_selected(metadata);
                sessionStorage.setItem('cluster', metadata?.name);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clusters, set_cluster_selected]);

    useEffect((): void => {
        (async (): Promise<void> => {
            user_info.role !== '' && cluster_selected?.name && datacenter_name ? await http_request('nodes', props, set_nodes, `/v1/datacenter/${datacenter_name}/cluster/${cluster_selected?.name}/nodes`, 0, user_info.role,
                user_info.subscription_id, datacenter_name, cluster_selected?.name) : destroy_worker('nodes');
        })();
        // eslint-disable-next-line
    }, [set_nodes, is_fresh, user_info.role, user_info.subscription_id, cluster_selected?.name]);

    useEffect((): void => {
        set_is_in_tabs(false);
    }, [set_is_in_tabs]);

    useEffect((): void => {
        set_table_desc([`${formatMessage({ id: 'nodes' })}`, `${formatMessage({ id: 'nodes_desc' })}`]);
    }, [set_table_desc, formatMessage]);

    useEffect((): void => {
        if (nodes && Array.isArray(nodes)) {
            const nodes_new: node_new[] = nodes.map(
                node => {
                    const metadata = (
                        ({
                            name, uid, selfLink, resourceVersion, creationTimestamp, usedCPU, usedMemory, usedRootfs, usedPod, usedDisk, usedGPU, totalCPU, totalMemory, totalRootfs, totalPod, totalDisk, totalGPU, limitCPU, limitMemory, requestCPU, requestMemory, labels
                        }) => ({
                            name, nodeType: labels?.nodetype, uid, selfLink, resourceVersion, creationTimestamp, cpu: { usedCPU, limitCPU, totalCPU, requestCPU }, memory: { usedMemory, limitMemory, totalMemory, requestMemory }, rootfs: { usedRootfs, limitsRootfs: totalRootfs }, pod: { usedPod, limitsPod: totalPod }, pvc: { usedDisk, limitsDisk: totalDisk }, gpu: { usedGPU, limitsGPU: totalGPU },
                        }))(node.metadata || {});
                    // const status = (
                    //     ({
                    //         cpu, memory, pods
                    //     }) => ({
                    //         cpu, memory, pods
                    //     }))(node.status.capacity);
                    return metadata;
                });
            set_items(nodes_new);
        }
    }, [set_items, nodes, is_fresh]);

    useEffect((): void => {
        set_card_selectable(false);
    }, [set_card_selectable]);

    useEffect(() => {
        set_search_title('node');
    }, [set_search_title]);

    return (
        <div className="no_pointer px-20 mt-20">
            <TableList />
        </div>
    )
}

export default injectIntl(NodesManagement);